<template>
  <v-row no-gutters>
    <v-col cols="12">
      <div class="upload-section">
        <v-progress-circular
          v-if="showPreloader"
          indeterminate
          color="primary"
          size="32"
          class="ml-4"></v-progress-circular>
        <v-btn
          v-if="props.hideFileName"
          variant="outlined"
          color="primary"
          prepend-icon="mdi-paperclip"
          :disabled="props.disabled || showPreloader"
          @click="pdfInput?.click()">
          {{ props.multiple ? "Choose Files" : "Choose File" }}
        </v-btn>
        <input
          ref="pdfInput"
          :loading="showPreloader"
          type="file"
          accept=".tif, .tiff, .pdf, .png, .jpg, .jpeg"
          :multiple="props.multiple"
          :disabled="props.disabled"
          :class="{ 'hidden-input': showPreloader, 'native-input-hidden': props.hideFileName }"
          @change="handleFileUpload" />
        <span v-if="progressText" class="text-body-2 text-medium-emphasis ml-2">{{ progressText }}</span>
        <v-alert v-if="uploadError" type="error" variant="tonal" density="compact" class="mt-2">
          {{ uploadError }}
        </v-alert>
      </div>
    </v-col>
  </v-row>
</template>

<script setup>
import { ref } from "vue";
import { uuid } from "vue-uuid";
import { uploadService, validateFile, parseUploadError } from "@/services/uploadService";

const props = defineProps({
  downloadDestination: {
    type: String,
    default() {
      return "";
    },
  },
  downloadLabel: {
    type: String,
    default() {
      return "";
    },
  },
  downloadDestinationIndex: {
    type: Number,
    default() {
      return null;
    },
  },
  disabled: {
    type: Boolean,
    default() {
      return false;
    },
  },
  // Replace the native file input with a plain "Choose File" button, so the browser's
  // selected-file name is not rendered next to it. For callers that list the uploaded
  // files themselves and would otherwise show the name twice.
  hideFileName: {
    type: Boolean,
    default() {
      return false;
    },
  },
  // Accept a whole selection at once, emitting one downloadLink per file in the order picked.
  // Off by default: callers that map one upload to one slot (supporting documents) expect a
  // single file per pick.
  multiple: {
    type: Boolean,
    default() {
      return false;
    },
  },
  // How many files this pick may contain, for callers with a cap of their own. The whole
  // selection is rejected before anything is uploaded — otherwise the overflow files would be
  // PUT to S3 and then dropped by the caller, leaving orphaned objects and no error.
  // null (default) means no limit.
  maxFiles: {
    type: Number,
    default() {
      return null;
    },
  },
});

const emit = defineEmits(["downloadLink", "uploadError"]);
const showPreloader = ref(false);
const pdfInput = ref(null);
const uploadError = ref(null);
// Only shown while a multi-file selection is in flight, so the user can see it progressing.
const progressText = ref("");

// Upload one file and announce it. Returns an error message, or null on success.
async function uploadOne(file) {
  const validationError = validateFile(file);
  if (validationError) return validationError;
  try {
    const { downloadUrl, uploadKey } = await uploadService.uploadFileWithKey(file, uuid.v4() + "/" + file.name);
    emit("downloadLink", {
      link: downloadUrl,
      storeVar: props.downloadDestination,
      label: props.downloadLabel,
      storeVarIndex: props.downloadDestinationIndex,
      fileName: file.name,
      uploadKey,
    });
    return null;
  } catch (error) {
    return parseUploadError(error);
  }
}

const handleFileUpload = async (event) => {
  const files = Array.from(event.target.files || []);
  if (files.length === 0) return;

  uploadError.value = null;
  if (props.maxFiles !== null && files.length > props.maxFiles) {
    uploadError.value =
      props.maxFiles > 0
        ? `You selected ${files.length} files but only ${props.maxFiles} more can be added. Select fewer files and try again.`
        : "No more files can be added here. Remove one first.";
    emit("uploadError", uploadError.value);
    if (pdfInput.value) pdfInput.value.value = "";
    return;
  }

  showPreloader.value = true;
  // Uploaded one at a time so the emitted order matches the selected order, and so a
  // multi-file pick doesn't fire a burst of presign + PUT round trips at once.
  const failures = [];
  for (const [index, file] of files.entries()) {
    progressText.value = files.length > 1 ? `Uploading ${index + 1} of ${files.length}…` : "";
    const failure = await uploadOne(file);
    if (failure) failures.push(files.length > 1 ? `${file.name}: ${failure}` : failure);
  }
  progressText.value = "";
  showPreloader.value = false;

  if (failures.length > 0) {
    uploadError.value = failures.join(" ");
    emit("uploadError", uploadError.value);
  }
  // Reset so re-picking the same file(s) fires @change again. Always safe when the native input
  // is hidden (the caller renders its own list); on a failure it also clears the stale selection.
  if ((props.hideFileName || failures.length > 0) && pdfInput.value) {
    pdfInput.value.value = "";
  }
};
</script>

<style lang="scss" scoped>
@import "../styles/components/_upload-file-component.scss";

.upload-loader {
  position: absolute;
  z-index: 1;
}

.hidden-input {
  opacity: 0;
  position: absolute;
  z-index: 0;
}

// Native input replaced by the "Choose File" button — kept in the DOM so it can be clicked
// programmatically, but never rendered (which is what hides the selected-file name).
.native-input-hidden {
  display: none;
}
</style>
